import React, { useCallback, useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExtensionIcon from '@mui/icons-material/Extension';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SyncIcon from '@mui/icons-material/Sync';

import { useNotification } from '../../hooks/NotificationContext';
import { API_BASE } from '../../services/api';
import eingangsbelegeService, {
  AnbieterKonto,
  ConnectorToken,
  PairingCreated,
  ProviderDefinition,
} from '../../services/eingangsbelegeService';

interface Props {
  open: boolean;
  account: AnbieterKonto | null;
  provider?: ProviderDefinition;
  onClose: () => void;
  onChanged: () => void;
}

const STEPS = ['Installieren', 'Verbinden', 'Testen', 'Fertig'];
const BRIDGE_SOURCE_IN = 'stark-aio-app';
const BRIDGE_SOURCE_OUT = 'stark-aio-connector';

function isFirefox(): boolean {
  return typeof navigator !== 'undefined' && /Firefox\//i.test(navigator.userAgent);
}

function versionFromXpiUrl(url?: string | null): string | null {
  const match = url?.match(/-(\d+\.\d+\.\d+(?:\.\d+)?)\.xpi(?:$|[?#])/i);
  return match?.[1] ?? null;
}

function installHref(url?: string | null): string | null {
  if (!url) return null;
  if (url.startsWith('/api/')) return `${API_BASE}${url.slice('/api'.length)}`;
  return url;
}

function compareVersions(left: string, right: string): number {
  const a = left.split('.').map((part) => Number.parseInt(part, 10) || 0);
  const b = right.split('.').map((part) => Number.parseInt(part, 10) || 0);
  const length = Math.max(a.length, b.length);
  for (let index = 0; index < length; index += 1) {
    const diff = (a[index] || 0) - (b[index] || 0);
    if (diff !== 0) return diff;
  }
  return 0;
}

const ConnectorEinrichtenDialog: React.FC<Props> = ({
  open,
  account,
  provider,
  onClose,
  onChanged,
}) => {
  const { showSuccess, showError } = useNotification();
  const [activeStep, setActiveStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [extensionDetected, setExtensionDetected] = useState(false);
  const [detectedVersion, setDetectedVersion] = useState<string | null>(null);
  const [pairing, setPairing] = useState<PairingCreated | null>(null);
  const [pairingState, setPairingState] = useState<'offen' | 'eingeloest' | 'abgelaufen'>();
  const [tokens, setTokens] = useState<ConnectorToken[]>([]);
  const [testMessage, setTestMessage] = useState('');

  const loadTokens = useCallback(async () => {
    if (!account) return;
    try {
      setTokens(await eingangsbelegeService.connectorTokens(account.id));
    } catch {
      setTokens([]);
    }
  }, [account]);

  const offeredVersion = versionFromXpiUrl(
    isFirefox() ? provider?.firefox_store_url : null,
  );
  const outdated = Boolean(
    detectedVersion && offeredVersion && compareVersions(detectedVersion, offeredVersion) < 0,
  );

  const sendViaChrome = useCallback(
    (message: Record<string, unknown>): Promise<any> =>
      new Promise((resolve, reject) => {
        const chromeRuntime = (window as any).chrome?.runtime;
        if (!provider?.extension_id || !chromeRuntime?.sendMessage) {
          reject(new Error('Erweiterung nicht erreichbar'));
          return;
        }
        let settled = false;
        const timeout = window.setTimeout(() => {
          if (!settled) {
            settled = true;
            reject(new Error('Keine Antwort der Erweiterung'));
          }
        }, 8000);
        try {
          chromeRuntime.sendMessage(
            provider.extension_id,
            message,
            (response: any) => {
              if (settled) return;
              settled = true;
              window.clearTimeout(timeout);
              const runtimeError = chromeRuntime.lastError;
              if (runtimeError) reject(new Error(runtimeError.message));
              else resolve(response);
            },
          );
        } catch (error) {
          if (!settled) {
            settled = true;
            window.clearTimeout(timeout);
            reject(error);
          }
        }
      }),
    [provider?.extension_id],
  );

  const sendViaBridge = useCallback(
    (message: Record<string, unknown>): Promise<any> =>
      new Promise((resolve, reject) => {
        const requestId = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;
        let settled = false;
        const timeout = window.setTimeout(() => {
          if (settled) return;
          settled = true;
          window.removeEventListener('message', onMessage);
          reject(new Error('Keine Antwort der Erweiterung'));
        }, 8000);
        const onMessage = (event: MessageEvent) => {
          if (event.source !== window || event.origin !== window.location.origin) return;
          if (event.data?.source !== BRIDGE_SOURCE_OUT || event.data?.requestId !== requestId) return;
          if (settled) return;
          settled = true;
          window.clearTimeout(timeout);
          window.removeEventListener('message', onMessage);
          resolve(event.data);
        };
        window.addEventListener('message', onMessage);
        window.postMessage(
          { source: BRIDGE_SOURCE_IN, requestId, ...message },
          window.location.origin,
        );
      }),
    [],
  );

  const sendExtensionMessage = useCallback(
    async (message: Record<string, unknown>) => {
      if (!isFirefox() && provider?.extension_id) {
        try {
          return await sendViaChrome(message);
        } catch {
          // Firefox und blockierte Chrome-Seiten nutzen die postMessage-Brücke.
        }
      }
      return sendViaBridge(message);
    },
    [provider?.extension_id, sendViaChrome, sendViaBridge],
  );

  useEffect(() => {
    if (!open) return;
    setActiveStep(0);
    setBusy(false);
    setExtensionDetected(false);
    setDetectedVersion(null);
    setPairing(null);
    setPairingState(undefined);
    setTestMessage('');
    loadTokens();

    let cancelled = false;
    (async () => {
      try {
        const response = await sendExtensionMessage({ type: 'STARK_AIO_PING' });
        if (cancelled || !response?.ok) return;
        setExtensionDetected(true);
        setDetectedVersion(typeof response.version === 'string' ? response.version : null);
      } catch {
        if (!cancelled) {
          setExtensionDetected(false);
          setDetectedVersion(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, loadTokens, sendExtensionMessage]);

  const detectExtension = async () => {
    try {
      setBusy(true);
      const response = await sendExtensionMessage({ type: 'STARK_AIO_PING' });
      if (!response?.ok) throw new Error('Ungültige Antwort');
      const version = typeof response.version === 'string' ? response.version : null;
      setExtensionDetected(true);
      setDetectedVersion(version);
      if (version && offeredVersion && compareVersions(version, offeredVersion) < 0) {
        showError(
          `Version ${version} ist veraltet. Bitte ${offeredVersion} installieren und die Seite neu laden.`,
        );
        return;
      }
      setActiveStep(1);
      showSuccess(version ? `Connector ${version} erkannt` : 'Connector erkannt');
    } catch {
      setExtensionDetected(false);
      setDetectedVersion(null);
      showError(
        'Connector wurde noch nicht erkannt. Nach einer Neuinstallation diese Seite neu laden und erneut prüfen.',
      );
    } finally {
      setBusy(false);
    }
  };

  const refreshPairing = useCallback(async () => {
    if (!account || !pairing) return;
    try {
      const status = await eingangsbelegeService.pairingStatus(account.id, pairing.id);
      setPairingState(status.status);
      if (status.status === 'eingeloest') {
        setActiveStep(2);
        await loadTokens();
        onChanged();
      }
    } catch {
      // Polling darf vorübergehende Netzwerkfehler tolerieren.
    }
  }, [account, pairing, loadTokens, onChanged]);

  useEffect(() => {
    if (!open || !pairing || pairingState === 'eingeloest' || pairingState === 'abgelaufen') return;
    const interval = window.setInterval(refreshPairing, 2000);
    return () => window.clearInterval(interval);
  }, [open, pairing, pairingState, refreshPairing]);

  const startPairing = async () => {
    if (!account) return;
    try {
      setBusy(true);
      const created = await eingangsbelegeService.createPairing(account.id);
      setPairing(created);
      setPairingState('offen');
      if (extensionDetected) {
        const response = await sendExtensionMessage({
          type: 'STARK_AIO_PAIR',
          apiOrigin: created.api_origin,
          pairingTicket: created.pairing_ticket,
          accountLabel: account.bezeichnung,
        });
        if (!response?.ok) {
          throw new Error(response?.error || 'Pairing wurde abgelehnt');
        }
        await refreshPairing();
      }
    } catch (error: any) {
      showError(error?.message || 'Pairing konnte nicht gestartet werden');
    } finally {
      setBusy(false);
    }
  };

  const runTest = async () => {
    try {
      setBusy(true);
      setTestMessage('');
      const response = await sendExtensionMessage({ type: 'STARK_AIO_SYNC_NOW' });
      if (!response?.ok) throw new Error(response?.error || 'Test fehlgeschlagen');
      setTestMessage(response.message || 'Synchronisierung wurde erfolgreich gestartet.');
      setActiveStep(3);
      showSuccess('Connector-Test erfolgreich');
    } catch (error: any) {
      setTestMessage(error?.message || 'Connector-Test fehlgeschlagen');
      showError(error?.message || 'Connector-Test fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (tokenId: number) => {
    if (!account) return;
    try {
      await eingangsbelegeService.revokeConnectorToken(account.id, tokenId);
      await loadTokens();
      onChanged();
      showSuccess('Connector-Zugriff widerrufen');
    } catch {
      // Axios-Interceptor zeigt Details.
    }
  };

  const copy = async (value: string) => {
    await navigator.clipboard.writeText(value);
    showSuccess('In die Zwischenablage kopiert');
  };

  if (!account) return null;

  const firefox = isFirefox();
  const storeUrl = firefox ? installHref(provider?.firefox_store_url) : provider?.store_url;
  const installLabel = firefox
    ? outdated && offeredVersion
      ? `Auf Version ${offeredVersion} aktualisieren`
      : 'Firefox-Add-on installieren'
    : 'Im Chrome Web Store installieren';
  const storeMissingText = firefox
    ? 'Der Firefox-Add-on-Eintrag ist noch nicht konfiguriert. Für den Pilotbetrieb steht die manuelle Installation unter „Nur für Administratoren“ zur Verfügung.'
    : 'Der Chrome-Web-Store-Eintrag ist noch nicht konfiguriert. Für den Pilotbetrieb steht die manuelle Installation unter „Nur für Administratoren“ zur Verfügung.';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ExtensionIcon color="primary" />
        Cursor-Connector einrichten
      </DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 3, mt: 1 }}>
          {STEPS.map((label) => (
            <Step key={label}><StepLabel>{label}</StepLabel></Step>
          ))}
        </Stepper>

        {activeStep === 0 && (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              Die Erweiterung liest ausschließlich Rechnungen aus Ihrer bereits angemeldeten
              Cursor-Browser-Session. Passwörter und Cookies werden nicht an STARK-AIO übertragen.
            </Alert>
            {extensionDetected && !outdated ? (
              <Alert
                severity="success"
                sx={{ mb: 2 }}
                action={
                  <Button color="inherit" size="small" onClick={() => setActiveStep(1)}>
                    Weiter
                  </Button>
                }
              >
                Version {detectedVersion || 'unbekannt'} ist installiert. Als Nächstes verbinden
                Sie dieses Konto.
              </Alert>
            ) : (
              <Box component="ol" sx={{ m: 0, pl: 2.5, '& > li': { mb: 2 } }}>
                <li>
                  {outdated && (
                    <Alert severity="warning" sx={{ mb: 1.5 }}>
                      Installiert ist Version {detectedVersion}. Aktuell ist Version {offeredVersion}.
                      Firefox ersetzt die vorhandene Erweiterung über denselben Link.
                    </Alert>
                  )}
                  {storeUrl ? (
                    <Button
                      variant="contained"
                      component={Link}
                      href={storeUrl}
                      target="_blank"
                      rel="noopener"
                      startIcon={<OpenInNewIcon />}
                    >
                      {installLabel}
                    </Button>
                  ) : (
                    <Alert severity="warning">{storeMissingText}</Alert>
                  )}
                  {storeUrl && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {firefox
                        ? `Firefox fragt oben nach der Erlaubnis.${offeredVersion ? ` Installiert wird Version ${offeredVersion}.` : ''}`
                        : 'Im Store auf „Hinzufügen“ klicken und die Installation bestätigen.'}
                    </Typography>
                  )}
                </li>
                <li>
                  <Button variant="outlined" onClick={() => window.location.reload()}>
                    Seite neu laden
                  </Button>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Erst nach dem Neuladen sieht STARK-AIO die Erweiterung. Den Assistenten
                    danach erneut öffnen — eine aktuelle Installation wird automatisch erkannt.
                  </Typography>
                </li>
                <li>
                  <Button onClick={detectExtension} disabled={busy} startIcon={busy ? <CircularProgress size={18} /> : <SyncIcon />}>
                    Installation prüfen
                  </Button>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Nur nötig, wenn die Seite bereits neu geladen wurde und der Connector
                    noch nicht erkannt ist.
                  </Typography>
                </li>
              </Box>
            )}
            <Accordion sx={{ mt: 1 }} disableGutters elevation={0} variant="outlined">
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2">Nur für Administratoren</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Button
                  variant="outlined"
                  href="/downloads/stark-aio-cursor-rechnungsconnector.zip"
                  startIcon={<ExtensionIcon />}
                  sx={{ mb: 1.5 }}
                >
                  Admin-/Test-Paket herunterladen
                </Button>
                <Typography variant="body2" color="text.secondary">
                  {firefox ? (
                    <>
                      ZIP entpacken, <strong>about:debugging#/runtime/this-firefox</strong> öffnen
                      und „Temporäres Add-on laden“ wählen (manifest.json). Temporäre Add-ons
                      verschwinden nach einem Firefox-Neustart. Für den dauerhaften Einsatz
                      das Firefox-Add-on über den Installationsbutton oben verwenden.
                    </>
                  ) : (
                    <>
                      ZIP entpacken, <strong>chrome://extensions</strong> bzw. <strong>edge://extensions</strong> öffnen,
                      Entwicklermodus aktivieren und „Entpackte Erweiterung laden“ wählen. Diese
                      Variante aktualisiert sich nicht automatisch.
                    </>
                  )}
                </Typography>
              </AccordionDetails>
            </Accordion>
          </Box>
        )}

        {activeStep === 1 && (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              {extensionDetected
                ? 'Erweiterung erkannt. Das Konto kann jetzt sicher verbunden werden.'
                : 'Fallback-Pairing: Code einmalig im Extension-Popup einfügen.'}
            </Alert>
            {!pairing ? (
              <Button variant="contained" onClick={startPairing} disabled={busy}>
                {busy ? <CircularProgress size={22} /> : 'Sicheres Pairing starten'}
              </Button>
            ) : (
              <Box>
                <Chip
                  label={
                    pairingState === 'eingeloest'
                      ? 'Verbunden'
                      : pairingState === 'abgelaufen'
                        ? 'Abgelaufen'
                        : 'Warte auf Erweiterung'
                  }
                  color={pairingState === 'eingeloest' ? 'success' : pairingState === 'abgelaufen' ? 'error' : 'warning'}
                  sx={{ mb: 2 }}
                />
                {pairingState !== 'eingeloest' && (
                  <>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Nur falls die automatische Übergabe nicht funktioniert:
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      label="STARK-AIO API-Origin"
                      value={pairing.api_origin}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <IconButton onClick={() => copy(pairing.api_origin)}>
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        ),
                      }}
                      sx={{ mb: 1.5 }}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Einmaliger Pairing-Code"
                      value={pairing.pairing_ticket}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <IconButton onClick={() => copy(pairing.pairing_ticket)}>
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        ),
                      }}
                    />
                  </>
                )}
              </Box>
            )}
          </Box>
        )}

        {activeStep === 2 && (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              Der Test öffnet die Cursor-Abrechnung. Falls die Session abgelaufen ist, melden
              Sie sich dort normal an und starten den Test erneut.
            </Alert>
            <Button variant="contained" onClick={runTest} disabled={busy} startIcon={<SyncIcon />}>
              {busy ? 'Test läuft…' : 'Test-Synchronisierung starten'}
            </Button>
            {testMessage && <Alert severity="warning" sx={{ mt: 2 }}>{testMessage}</Alert>}
          </Box>
        )}

        {activeStep === 3 && (
          <Alert severity="success">
            Der Connector ist eingerichtet. Automatische Prüfungen können im Erweiterungs-Popup
            aktiviert werden. Der Zugriff ist jederzeit hier widerrufbar.
          </Alert>
        )}

        <Divider sx={{ my: 3 }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Verbundene Browser</Typography>
        {tokens.length === 0 ? (
          <Typography variant="body2" color="text.secondary">Noch kein Browser verbunden.</Typography>
        ) : (
          <List dense>
            {tokens.map((token) => (
              <ListItem key={token.id} disableGutters>
                <ListItemText
                  primary={token.geraet_name}
                  secondary={`Version ${token.connector_version || 'unbekannt'} · ${
                    token.letzte_nutzung_am
                      ? `zuletzt ${new Date(token.letzte_nutzung_am).toLocaleString('de-DE')}`
                      : 'noch nicht verwendet'
                  }${token.widerrufen_am ? ' · widerrufen' : ''}`}
                />
                {!token.widerrufen_am && (
                  <ListItemSecondaryAction>
                    <IconButton edge="end" color="error" onClick={() => revoke(token.id)}>
                      <DeleteOutlineIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                )}
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        {activeStep === 0 && !extensionDetected && (
          <Button onClick={() => setActiveStep(1)}>Manuelles Pairing</Button>
        )}
        {activeStep === 3 && (
          <Button onClick={onClose} variant="contained">Fertig</Button>
        )}
        <Button onClick={onClose}>Schließen</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConnectorEinrichtenDialog;
